/**
 * TaskListRow Component
 * Individual task row for the phase accordion list view
 * MED-162: Updated styling per QA feedback
 * MED-479: Inline subtask expansion with chevron toggle
 */

import { useState } from 'react';
import { Group, Text, Badge, Checkbox, Avatar, ActionIcon, Menu, Box, Collapse, Loader, Stack } from '@mantine/core';
import { Calendar, MessageSquare, MoreVertical, Edit3, Trash2, Copy, ChevronRight, ChevronDown, Link as LinkIcon } from 'react-feather';
import { Tooltip } from '@mantine/core';
import type { TaskWithDetails, TaskStatus } from '../../../../types';
import { getStatusConfig, getPriorityConfig } from '../../../../config/taskConfig';
import { formatDateOnly } from '../../../../utils/formatters';
import { isDateOnlyPast } from '../../../../utils/dateHelpers';
import { getAvatarColor, getInitials } from '../../../../utils/avatarHelpers';
import { copyTaskLink } from '../../../../utils/taskLinks';

interface TaskListRowProps {
  task: TaskWithDetails;
  onToggleComplete: (taskId: string, currentStatus: TaskStatus) => void;
  onClick: (task: TaskWithDetails) => void;
  onEdit?: (task: TaskWithDetails) => void;
  onDelete?: (taskId: string) => void;
  onDuplicate?: (taskId: string) => void;
  /** Whether this task's subtasks are expanded */
  isExpanded?: boolean;
  /** Whether subtasks are currently loading */
  isLoadingSubtasks?: boolean;
  /** Callback to toggle subtask expansion */
  onToggleExpand?: (taskId: string) => void;
  /** Cached subtasks for this task */
  subtasks?: TaskWithDetails[];
  /** Left padding for indented subtask rows */
  indent?: number;
  /** MED-770: Cohort ID for generating copy-link URLs */
  cohortId?: string;
}

export function TaskListRow({
  task,
  onToggleComplete,
  onClick,
  onEdit,
  onDelete,
  onDuplicate,
  isExpanded = false,
  isLoadingSubtasks = false,
  onToggleExpand,
  subtasks,
  indent = 0,
  cohortId,
}: TaskListRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isComplete = task.status === 'complete';
  const statusConfig = getStatusConfig(task.status);
  const priorityConfig = getPriorityConfig(task.priority);
  const showPriority = task.priority === 'high' || task.priority === 'urgent';
  const hasSubtasks = task.subtask_count > 0;

  // Check if overdue
  const isOverdue =
    task.due_date &&
    isDateOnlyPast(task.due_date) &&
    task.status !== 'complete' &&
    task.status !== 'cancelled';

  // Get avatar color based on assignee ID or name
  const avatarColor = task.assignee_id
    ? getAvatarColor(task.assignee_id)
    : getAvatarColor(task.assignee_name || 'unassigned');

  return (
    <>
      <Box
        style={{
          borderLeft: isHovered ? '3px solid #28463A' : '3px solid transparent',
          backgroundColor: isHovered ? '#F5F7F6' : 'transparent',
          transition: 'all 0.15s ease',
          cursor: 'pointer',
          paddingLeft: indent > 0 ? indent : undefined,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Group
          gap="xl"
          py={indent > 0 ? 'xs' : 'lg'}
          px="lg"
          style={{
            paddingLeft: isHovered ? 'calc(var(--mantine-spacing-lg) - 3px)' : 'var(--mantine-spacing-lg)',
          }}
        >
          {/* Expand/Collapse Chevron */}
          {hasSubtasks && onToggleExpand ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(task.id);
              }}
            >
              {isExpanded ? (
                <ChevronDown size={16} color="var(--mantine-color-dimmed)" />
              ) : (
                <ChevronRight size={16} color="var(--mantine-color-dimmed)" />
              )}
            </ActionIcon>
          ) : (
            <Box w={34} /> // Spacer for alignment
          )}

          {/* Checkbox */}
          <Checkbox
            checked={isComplete}
            color="medleyGreen"
            size="sm"
            onChange={(e) => {
              e.stopPropagation();
              onToggleComplete(task.id, task.status);
            }}
            onClick={(e) => e.stopPropagation()}
            styles={{
              input: {
                cursor: 'pointer',
              },
            }}
          />

          {/* Task Name + Subtask Count + Copy Link */}
          <Group
            gap={8}
            style={{ flex: 1, cursor: 'pointer' }}
            onClick={() => onClick(task)}
          >
            <Text
              size="sm"
              style={{
                textDecoration: isComplete ? 'line-through' : 'none',
                color: isComplete ? 'var(--mantine-color-dimmed)' : '#28463A',
              }}
            >
              {task.title}
            </Text>
            {hasSubtasks && (
              <Text size="xs" c="dimmed">
                ({task.subtasks_completed}/{task.subtask_count})
              </Text>
            )}
            {/* MED-770: Copy link — positioned right of title/subtask count */}
            {cohortId && isHovered && (
              <Tooltip label="Copy link to task" position="top" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyTaskLink(task.id, cohortId);
                  }}
                >
                  <LinkIcon size={14} />
                </ActionIcon>
              </Tooltip>
            )}
            {task.comment_count > 0 && (
              <Badge
                size="sm"
                color="blue"
                variant="light"
                leftSection={<MessageSquare size={10} />}
              >
                {task.comment_count}
              </Badge>
            )}
          </Group>

          {/* Priority Badge - only show for HIGH and URGENT */}
          {showPriority && (
            <Badge
              size="xs"
              color={priorityConfig.color}
              variant="light"
            >
              {priorityConfig.label.toUpperCase()}
            </Badge>
          )}

          {/* Subtasks Overdue Badge - only on parent tasks with overdue subtasks */}
          {!task.parent_task_id && task.has_overdue_subtasks && (
            <Badge
              size="xs"
              color="dangerRed"
              variant="light"
            >
              SUBTASKS OVERDUE
            </Badge>
          )}

          {/* Due Date */}
          {task.due_date && (
            <Group gap={4}>
              <Calendar size={12} color={isOverdue ? '#D64545' : '#555555'} />
              <Text
                size="xs"
                fw={isOverdue ? 600 : 400}
                style={{ color: isOverdue ? '#D64545' : '#555555' }}
              >
                {formatDateOnly(task.due_date)}
              </Text>
            </Group>
          )}

          {/* Assignee */}
          {task.assignee_name ? (
            <Group gap={6}>
              <Avatar
                size="sm"
                radius="xl"
                styles={{
                  root: { backgroundColor: avatarColor },
                  placeholder: { backgroundColor: avatarColor, color: '#FFFFFF' },
                }}
              >
                {getInitials(task.assignee_name)}
              </Avatar>
              <Text size="xs" style={{ color: '#28463A' }}>
                {task.assignee_name}
              </Text>
            </Group>
          ) : (
            <Text size="xs" style={{ color: '#555555', minWidth: 80 }}>
              Unassigned
            </Text>
          )}

          {/* Status Badge */}
          <Badge
            size="xs"
            color={statusConfig.color}
            variant={statusConfig.variant}
            styles={{ root: { minWidth: 70, ...(task.status === 'not_started' ? { border: 'none', color: '#888888' } : {}) } }}
          >
            {statusConfig.label.toUpperCase()}
          </Badge>

          {/* Actions Menu */}
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {onEdit && (
                <Menu.Item
                  leftSection={<Edit3 size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}
                >
                  Edit Task
                </Menu.Item>
              )}
              {/* Only show Duplicate for parent tasks (not subtasks) */}
              {onDuplicate && !task.parent_task_id && (
                <Menu.Item
                  leftSection={<Copy size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate(task.id);
                  }}
                >
                  Duplicate
                </Menu.Item>
              )}
              {onDelete && (
                <Menu.Item
                  leftSection={<Trash2 size={14} />}
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(task.id);
                  }}
                >
                  Delete Task
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Box>

      {/* Subtask expansion */}
      {hasSubtasks && onToggleExpand && (
        <Collapse in={isExpanded}>
          <Box style={{ borderLeft: '2px solid var(--mantine-color-gray-2)', marginLeft: 24 }}>
            {isLoadingSubtasks ? (
              <Group py="xs" px="lg" gap="xs">
                <Loader size="xs" />
                <Text size="xs" c="dimmed">Loading subtasks...</Text>
              </Group>
            ) : subtasks && subtasks.length > 0 ? (
              <Stack gap={0}>
                {subtasks.map((subtask) => (
                  <TaskListRow
                    key={subtask.id}
                    task={subtask}
                    onToggleComplete={onToggleComplete}
                    onClick={onClick}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    indent={1}
                    cohortId={cohortId}
                  />
                ))}
              </Stack>
            ) : null}
          </Box>
        </Collapse>
      )}
    </>
  );
}

export default TaskListRow;
